// src/components/CreateCampaignForm.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase/client';
import TargetAudienceSelector from './TargetAudienceSelector';
import type { TargetAudienceData } from '../hooks/useTargetAudience';
// Header is provided by AuthenticatedRoute wrapper

interface Props {
  onCreated?: () => void;
}

type Step = 'name' | 'target-audience' | 'review';

const CreateCampaignForm: React.FC<Props> = ({ onCreated }) => {
  const [currentStep, setCurrentStep] = useState<Step>('name');
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [campaignName, setCampaignName] = useState('');
  const [targetAudienceData, setTargetAudienceData] = useState<TargetAudienceData>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [authLoading, setAuthLoading] = useState(true);
  const navigate = useNavigate();
  
  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/login');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/login');
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const steps = [
    { id: 'name', title: 'Campaign Name', description: 'Give your campaign a name' },
    { id: 'target-audience', title: 'Target Audience', description: 'Choose your target audience' },
    { id: 'review', title: 'Review & Submit', description: 'Review and submit your campaign' }
  ];

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Checking authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  const currentStepIndex = steps.findIndex(step => step.id === currentStep);

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!campaignName.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('campaigns')
        .insert({
          name: campaignName.trim(),
          status: 'draft',
        })
        .select()
        .single();

      if (error) throw error;

      setCampaignId(data.id);
      setCurrentStep('target-audience');
    } catch (err) {
      console.error('Campaign creation error:', err);
      setError(err instanceof Error ? err.message : 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleTargetAudienceNext = () => {
    const isComplete = (targetAudienceData.type === 'generic_filter' && targetAudienceData.filter) ||
                      (targetAudienceData.type === 'csv_upload' && targetAudienceData.fileUrl);
    
    if (isComplete) {
      setCurrentStep('review');
    }
  };

  const handleFinalSubmit = async () => {
    if (!campaignId) return;

    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('campaigns')
        .update({ status: 'submitted' })
        .eq('id', campaignId);

      if (error) throw error;

      // Call onCreated callback if provided
      if (onCreated) {
        onCreated();
        // If callback provided, stay on edit page for continued editing
        navigate(`/campaign/${campaignId}/edit`);
      } else {
        // If no callback (standalone route), redirect to home after completion
        navigate('/');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep === 'target-audience') {
      setCurrentStep('name');
    } else if (currentStep === 'review') {
      setCurrentStep('target-audience');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'name':
        return (
          <form onSubmit={handleNameSubmit} className="space-y-6">
            <div>
              <label htmlFor="campaign-name" className="block text-sm font-medium text-gray-700 mb-2">
                Campaign Name *
              </label>
              <input
                type="text"
                id="campaign-name"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="Enter campaign name"
                required
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading || !campaignName.trim()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Next: Target Audience'}
              </button>
            </div>
          </form>
        );

      case 'target-audience':
        return campaignId ? (
          <div className="space-y-6">
            <TargetAudienceSelector
              campaignId={campaignId}
              onChange={setTargetAudienceData}
            />
            
            <div className="flex justify-between">
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Back
              </button>
              
              <button
                type="button"
                onClick={handleTargetAudienceNext}
                disabled={!targetAudienceData.type}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next: Review
              </button>
            </div>
          </div>
        ) : null;

      case 'review':
        return (
          <div className="space-y-6">
            {/* Campaign Summary */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Campaign Summary</h4>
              
              <div className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-600">Campaign Name</dt>
                  <dd className="text-sm text-gray-900 mt-1">{campaignName}</dd>
                </div>
                
                <div>
                  <dt className="text-sm font-medium text-gray-600">Target Audience</dt>
                  <dd className="text-sm text-gray-900 mt-1">
                    {targetAudienceData.type === 'generic_filter' && targetAudienceData.filter && (
                      <span>
                        Generic Filter: {targetAudienceData.filter.region}, {targetAudienceData.filter.industry}, {targetAudienceData.filter.position}
                      </span>
                    )}
                    {targetAudienceData.type === 'csv_upload' && targetAudienceData.fileName && (
                      <span>CSV Upload: {targetAudienceData.fileName}</span>
                    )}
                  </dd>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Back
              </button>
              
              <button
                type="button"
                onClick={handleFinalSubmit}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting...' : 'Submit Campaign'}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Create Campaign</h1>
              <p className="text-gray-600">Set up your new marketing campaign</p>
            </div>
            <button
              onClick={() => navigate('/campaigns')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Campaigns
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stepper */}
        <div className="mb-8">
        <nav aria-label="Progress">
          <ol className="flex items-center">
            {steps.map((step, index) => (
              <li key={step.id} className={`relative ${index !== steps.length - 1 ? 'pr-8 sm:pr-20' : ''}`}>
                {/* Connector line */}
                {index !== steps.length - 1 && (
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className={`h-0.5 w-full ${index < currentStepIndex ? 'bg-blue-600' : 'bg-gray-200'}`} />
                  </div>
                )}
                
                <div className="relative flex items-center justify-center">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full ${
                      index < currentStepIndex
                        ? 'bg-blue-600 text-white'
                        : index === currentStepIndex
                        ? 'border-2 border-blue-600 bg-white text-blue-600'
                        : 'border-2 border-gray-300 bg-white text-gray-500'
                    }`}
                  >
                    {index < currentStepIndex ? (
                      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <span className="text-sm font-medium">{index + 1}</span>
                    )}
                  </div>
                </div>
                
                <div className="mt-2 text-center">
                  <div className={`text-sm font-medium ${index <= currentStepIndex ? 'text-gray-900' : 'text-gray-500'}`}>
                    {step.title}
                  </div>
                  <div className="text-xs text-gray-500">{step.description}</div>
                </div>
              </li>
            ))}
          </ol>
        </nav>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

        {/* Step Content */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
};

export default CreateCampaignForm;



